import React, { useState, useEffect, useContext, useCallback, useRef, MouseEvent, SyntheticEvent, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Material UI Core
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import { Tabs, Tab, useTheme, styled } from "@mui/material";

// Custom Components
import { GridContainer, GridItem } from "@/ui/Grid";
import SnackbarContext from "@/ui/SnackbarContext/SnackbarContext";
import OperationsContext, { OperationsContextType } from "@/components/OperationsContext/OperationsContext";

import ObjectAnalysisToolbar from "./ObjectAnalysisToolbar/ObjectAnalysisToolbar";
import ObjectAnalysisMiniMap from "./ObjectAnalysisMiniMap";
import ObjectAnalysisThumbs from "./ObjectAnalysisThumbs";
import ObjectAnalysisImage, { Clip } from "./ObjectAnalysisImage";
import ObjectAnalysisImageCrop from "./ObjectAnalysisImageCrop";
import ObjectAnalysisKatoviTable from "./ObjectAnalysisKatoviTable";
import ObjectAnalysisDijeloviTable from "./ObjectAnalysisDijeloviTable";
import ObjectAnalysisInfo from "./ObjectAnalysisInfo"
import ObjectAnalysisKNTable from "./ObjectAnalysisKNTable"

import modelObjekti from "@/models/objekti";
import modelDijelovi from "@/models/dijelovi";
import modelKN from "@/models/kn";
import modelZgrade from "@/models/zgrade";

import { ActPromiseBoolean, IResolve, IReject } from "@/lib/ActPromise";
import dataController from "@/lib/dataController";
import appValidator from "@/lib/appValidator";
import confirmDialog from '@/lib/confirmDialog';
import dijeloviService from '@/services/dijeloviService';
import objektiService from "@/services/objektiService";
import imgService from "@/services/imgService";

//Types
import { SnackbarContextType } from "@/@types/ui/SnackbarContext";
import { DCRecord, DCFieldValue } from "@/@types/lib/dataController";
import { ConfirmResult } from "@/@types/lib/confirmDialog";
import AppPage from "@/ui/AppPage/AppPage";
import ObjectAnalysisBuildingsTable from "./ObjectAnalysisBuildingsTable";
import { TabContext, TabPanel } from "@mui/lab";
import { Geometry } from "ol/geom";
import OlSourceVector from "ol/source/Vector";
import OlFormatWKT from "ol/format/WKT";
import UserContext from "@/components/UserContext/UserContext";
import { Permissions } from "@/lib/permissions";

//@ts-ignore
let TIMEOUT = null;
const NAMJENA = { 1: "S", 2: "P", 3: "G", 4: "B", 5: "K", 6: "J", 7: "A", 8: "O", 9: "T", 10: "Z", 11: "F", 12: "N", };

type Tuple<T> = [T, T];
type B2BFn = (val?: boolean) => IResolve<boolean> | Promise<boolean>

const OperationsObjectAnalysisPage = React.memo(() => {
//#region defs
  const snackbarContext = useContext(SnackbarContext) as SnackbarContextType;
  const operationsContext = useContext(OperationsContext) as OperationsContextType;
  const userContext = useContext(UserContext);

  const { t } = useTranslation();

  const location = useLocation();
  const theme = useTheme();

  const validator = new appValidator(t);
  const [records, setRecords] = useState<Array<DCRecord>>([]);

  const dioState = useRef<'done' | 'pending'>('done');
  const editingDio = useRef<DCRecord | null>(null);
  const tmpEditingDio = useRef<DCRecord | null>(null);
  const originalDijelovi = useRef<DCRecord[]>([]);

  const katRecord = useRef<DCRecord | null>(null);

  const dontRefresh = useRef<boolean>(false);
  const locationUsed4Img = useRef<boolean>(false);
  const locationUsed4Obj = useRef<boolean>(false);

  const napomena = useRef<Tuple<DCFieldValue>>([null, null] as Tuple<DCFieldValue>);
  const [napomenaRC, setNapomenaRC] = useState<number>(0);

  const [imagesBasicInfo, setImagesBasicInfo] = useState<Array<DCRecord>>([]); // hook 19
  const [selectedRecord, setSelectedRecord] = useState<DCRecord | null>(null);
  const [selectedImage, setSelectedImage] = useState<DCRecord | null>(null);
  const [selectedImageId, setSelectedImageId] = useState<number>(-1);
  const [selectedImageInfo, setSelectedImageInfo] = useState<DCRecord | null>(null);
  const [cropClip, setCropClip] = useState<Clip | null>(null);
  const [largeCanvas, setLargeCanvas] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);

  const [highlightedSource, setHighlightedSource] = useState<OlSourceVector<Geometry>>(new OlSourceVector({}));
  const [measurementsSource, setMeasurementsSource] = useState<OlSourceVector<Geometry>>(new OlSourceVector({}));

  const [katId, setKatId] = useState<number>(-1); // hook 26
  const [updatedKatDijelovi, setUpdatedKatDijelovi] = useState<DCRecord[]>([]);
  const [dioRevisionCounter, setDioRevisionCounter] = useState(0);

  const [thumbsLoaded, setThumbsLoaded] = useState<boolean>(false);

  const [showLargeMap, setShowLargeMap] = useState(false);
  const [tabValue, setTabValue] = useState('kn');

  const [mouseUp, setMouseUp] = useState<MouseEvent<HTMLDivElement>>()

  const [KNRecords, setKNRecords] = useState<Array<DCRecord>>([]);
  const [zgradeRecords, setZgradeRecords] = useState<Array<DCRecord>>([]);
  const [currentZgradaId, setCurrentZgradaId] = useState<number | null>(null);

  const MAP_ID = 81;
  const tablesMinHeight = showLargeMap ? 0 : 525;

  const baseRecordPath = "operations/objekti/"
  const dc = new dataController(modelObjekti, baseRecordPath);

  const knRecordPath = `operations/projekti/${selectedRecord?.projekt_id}/kn/${selectedRecord?.lokacija_id}`;
  const dcKN = new dataController(modelKN, knRecordPath);

  const zgradeRecordPath = `operations/projekti/${selectedRecord?.projekt_id}/zgrade/${selectedRecord?.id}`;
  const dcZgrade = new dataController(modelZgrade, zgradeRecordPath);

  const StyledTab = styled(Tab)({
    "&.Mui-selected": {
      color: theme.palette.secondary.dark
    }
  });

  const isPregledanOrPassedQC = useMemo(() => {
    const isAdmin = userContext?.hasAnyPermission([Permissions.Administration]);
    if (selectedRecord) {
      return !!selectedRecord.pregledano || (!isAdmin && !!selectedRecord.qc_check)
    } else return false
  }, [selectedRecord, userContext])

//#endregion
//#region functions

  const handleRecordSelect = (id: number, record: DCRecord | null) => {
    //console.time("handleRecordSelect");
    return checkIfDioChanged()
      .then((canContinue: boolean) => {
        if (!canContinue) {
          saveEditingDio().catch((error) => {
            snackbarContext.showNotification(`Pogreška kod spremanja dijela! ${JSON.stringify(error)}`, "error", true);
          })
        }
        return checkIfNapomenaChanged()
          .then(canContinue => {
            if (!canContinue) {
              saveNapomena().catch(error => {
                snackbarContext.showNotification(`Pogreška kod spremanja napomene! ${JSON.stringify(error)}`, "error", true);
              })
            }
            handleRecordSelectWrapped(id, record);
          })
          .finally(() => {
            napomena.current = [null, null];
          });
      })
      .catch(() => {
        console.error("SHOULDN'T HAPPEN!!!");
        handleRecordSelectWrapped(id, record);
      });
  }

  const handleRecordSelectWrapped = (id: number, record: DCRecord | null) => {
    if (selectedRecord !== null && id === selectedRecord.id) {
      //@ts-ignore
      OP3_CALC.resetValues();
      setSelectedRecord(null);
    } else if (id !== -1 && id !== null) {
      // loaderContext.toggleLoading(true);
      dc.GetDataSingle(id).then((resp) => {
        if (resp.success) {
          if (resp.data) {
            const { broj_katova, broj_dijelova, pre_pb } = resp.data as DCRecord;
            //@ts-ignore
            OP3_CALC.setValues(broj_katova, broj_dijelova, pre_pb);
          } else {
            //@ts-ignore
            OP3_CALC.resetValues();
          }
          let imagesState: DCRecord[] = [];
          if (resp.data && 'images' in (resp.data as DCRecord)) {
            const { images } = resp.data as DCRecord;
            if (images && Array.isArray(images)) {
              imagesState = (images as DCRecord[]).map((img: DCRecord, i: number) => ({
                ...img,
                id: img.image_id as number,
                objektId: (resp.data as DCRecord).id,
                imageId: img.image_id,
                ordinal: i + 1,
                wkt: img.yaw_wkt,
                type: "image-basic-info"
              } as DCRecord));
            }
          }
          // make both set at once
          setSelectedRecord(() => {
            setImagesBasicInfo(imagesState);
            return resp.data as DCRecord
          });
        }
      });
    } else {
      //@ts-ignore
      OP3_CALC.resetValues();
      setSelectedRecord(null);
    }
    setSelectedImage(prev => prev !== null ? null : prev);
    setCropClip(prev => prev !== null ? null : prev);
    setKatId(prev => prev !== -1 ? -1 : prev);
    setSelectedImageId(prev => prev !== -1 ? -1 : prev);
  };

  const handleRecordChange = () => {
    refreshRecords();
  }

  const setImageFavorite = useCallback((imageId: number, isFav: boolean) => {
    if (selectedRecord === null) return;
    imgService.setObjImageFav(selectedRecord.id as number, imageId, isFav).then((resp) => {
      if (resp.success) {
        setImagesBasicInfo(prev => prev.map((img: DCRecord) => (
          img.id === imageId ? { ...img, selected: isFav } : img
        )))
      } else {
        console.error(resp);
      }
    })
  }, [selectedRecord])

  const handleMarkFavorite = useCallback((image: DCRecord) => {
    //console.log(image)
    setImageFavorite(image.id as number, !(image.selected as boolean));
  }, [selectedRecord]);

  const handleCropSaved = useCallback((imageId: number) => {
    //console.log("on crop saved!")
    // setMarkImageIdAsFav(imageId);
    setImageFavorite(imageId, true);
    setCropClip(null);
  }, [selectedRecord]);

  const handleImageSelect = useCallback((id: number, image: DCRecord | null) => {
    if (image !== null) {
      setSelectedImage(image);
      setCropClip(null);
    }
    setSelectedImageId(-1);
  }, [])

  const handleImageInfoSelect = useCallback((id: number | null, imageInfo: DCRecord | null) => {
    setTabValue('snimke');
    
    if (id) {
      setSelectedImageInfo(imageInfo);
    } else {
      setSelectedImageInfo(null);
    }
  }, [])

  const openSwalDioNapomena = (dio: boolean, napom: boolean) => {
    const { id: objId } = selectedRecord || {};
    const { broj_dijela: dioBr } = editingDio.current || {};
    const { broj_kata: katBr } = katRecord.current || {};
    let title: string = "";
    if (!dio && !napom) {
      title = t("cdialogs.changes_dio_napomena", { objId });
    } else if (!dio) {
      title = t("cdialogs.changes_dio", { dioBr, katBr, objId });
    } else if (!napom) {
      title = t("cdialogs.changes_napomena", { objId, dioBr });
    }
    const text = t("cdialogs.revise_data");
    const type = "warning";
    // const confirmButtonText = t("cdialogs.yes_save_changes");
    const cancelButtonText = t("cdialogs.just_close");

    return confirmDialog(title, text, type, "", cancelButtonText, { allowEscapeKey: false, showCloseButton: false, showConfirmButton: false });
  }

  const isEmpty = (value: any) => {
    return value === null || value === undefined || value === "";
  }

  const checkIfNapomenaChanged = (shouldOpenSwal: boolean = true) => {
    const [newN, oldN] = napomena.current;
    if (isEmpty(oldN) && isEmpty(newN)) {
      return Promise.resolve(true);
      //@ts-ignore
    } else if (isEmpty(oldN) ^ isEmpty(newN) || oldN !== newN) { // this is xor
      if (shouldOpenSwal) {
        return confirmCloseNapomena()
          .then(result => {
            if (result.canceled) {
              return Promise.resolve(true);
            } else if (result.confirmed) {
              return Promise.resolve(false);
            }
            console.error("SHOULDN'T HAPPEN!!!");
            return Promise.resolve(true);
          })
          .catch(() => {
            return Promise.resolve(true);
          });
      } else {
        return Promise.resolve(false);
      }
    } else {
      return Promise.resolve(true);
    }
  };

  const saveNapomena = () => {
    const [newNapomena, _] = napomena.current;
    if (!selectedRecord) return Promise.reject({ msg: "record not selected" });
    return objektiService.partiallyUpdateObjekt(selectedRecord.id as number, {
      napomena: newNapomena,
      // gl_obj,
    }).then((result) => {
      if (result.success) {
        // console.log('!! Great success')
        saveNapomenaValue(newNapomena);
        setNapomenaRC(rc => rc + 1);
      } else if (result.error) {
        const msg = "Greška prilikom ažuriranja podataka! " + result.error;
        snackbarContext.showNotification(msg, "error");
      } else if (result.validationPass === false) {
        //do nothing
      }
    })
  };

  const confirmCloseNapomena = (): Promise<ConfirmResult> => {
    const [newN, oldN] = napomena.current;
    const { id: objId } = selectedRecord || {};
    const title = t("cdialogs.changes_napomena", { objId });
    const text = t("cdialogs.want_to_save_napomena");
    const type = "warning";
    const confirmButtonText = t("cdialogs.yes_save_changes");
    const cancelButtonText = t("cdialogs.no_just_close_napomena");

    const html = `
      <div key="namjena" style="text-align: start;">
      <div style="display: inline-block; width: 150px; text-align: end;">${t('objekti.napomena')}:</div>
      &nbsp;
      <span style="background-color: rgba(255,0,0,0.3);">${oldN}</span>
      &rarr;
      <span style="background-color: rgba(0,255,0,0.3);">${newN}</span>
      </div>
    `

    return confirmDialog(title, text, type, confirmButtonText, cancelButtonText, { allowEscapeKey: false, showCloseButton: false, html });
  }

  const getNamjenaLabel = (namjena: DCFieldValue | null) => {
    if (!namjena)
      return " ";
    if (typeof namjena === 'object' && 'label' in namjena)
      return namjena.label;
    //@ts-ignore
    if (NAMJENA[namjena])
      //@ts-ignore
      return NAMJENA[namjena];
    return " ";
  }

  const getDioDiff = () => {
    const originalDio = originalDijelovi.current.find(dio => dio.id === (editingDio.current && editingDio.current.id as number));
    if (!originalDio) return [];
    const changes = validator.getRecordChanges(editingDio.current as DCRecord, originalDio as DCRecord);
    const ndc = new dataController(modelDijelovi)
    return Object.keys(changes).map((key: string) => {
      const [newVal, originalVal] = changes[key];
      //@ts-ignore
      const originalRealVal = key === 'namjena_id' ? getNamjenaLabel(originalVal) : originalVal;
      //@ts-ignore
      const newRealVal = key === 'namjena_id' ? getNamjenaLabel(newVal) : newVal;
      const field = ndc.getField(key);
      return (`
        <div key=${key} style="text-align: start;">
        <div style="display: inline-block; width: 150px; text-align: end;">${t(field ? field.ttoken : "")}:</div>
        &nbsp;
        <span style="background-color: rgba(255,0,0,0.3);">${originalRealVal}</span>
        &rarr;
        <span style="background-color: rgba(0,255,0,0.3);">${newRealVal}</span>
        </div>
      `);
    })
  }

  const confirmCloseDio = (): Promise<ConfirmResult> => {
    const { broj_dijela: dioBr } = editingDio.current || {};
    const { broj_kata: katBr } = katRecord.current || {};
    const { id: objId } = selectedRecord || {};
    const title = t("cdialogs.changes_dio", { dioBr, katBr, objId });
    const text = t("cdialogs.want_to_save_dio");
    const type = "warning";
    const confirmButtonText = t("cdialogs.yes_save_changes");
    const cancelButtonText = t("cdialogs.no_just_close_dio");

    const html = getDioDiff();

    return confirmDialog(title, text, type, confirmButtonText, cancelButtonText, { allowEscapeKey: false, showCloseButton: false, html: html.join('\n') });
  }

  const checkIfDioChanged: B2BFn = (shouldOpenSwal: boolean = true) => {
    if (editingDio.current !== null) {
      const originalDio = originalDijelovi.current.find(dio => dio.id === (editingDio.current!.id as number));
      if (!originalDio) {
        return ActPromiseBoolean.resolve(true);
      }
      if (!validator.checkIfRecordChanged(originalDio, editingDio.current)) {
        return ActPromiseBoolean.resolve(true);
      }
      if (shouldOpenSwal) {
        return confirmCloseDio()
          .then(result => {
            if (result.canceled) {
              return Promise.resolve(true);
            } else if (result.confirmed) {
              return Promise.resolve(false);
            }
            console.error("SHOULDN'T HAPPEN!!!");
            return Promise.resolve(true);
          })
          .catch(() => {
            return Promise.resolve(true);
          });
      } else {
        return ActPromiseBoolean.resolve(false);
      }
    } else {
      return ActPromiseBoolean.resolve(true);
    }
  }

  const saveEditingDio = (refreshDijelovi: boolean = false) => {
    if (editingDio.current === null) return Promise.resolve();
    const { id: dioId, namjena_id, pre_pb, broj_dijela } = editingDio.current as DCRecord;
    const namjenaValue = namjena_id
      && typeof namjena_id === 'object'
      && 'value' in namjena_id
      ? namjena_id.value
      : namjena_id;

    return dijeloviService.partiallyUpdateDio(
      selectedRecord!.id as number,
      katId as number,
      dioId as number,
      {
        namjena_id: namjenaValue,
        pre_pb: pre_pb ? parseInt(pre_pb as string, 10) : 0,
        broj_dijela: broj_dijela ? parseInt(broj_dijela as string, 10) : 0
      })
      .then(resp => {
        if (resp.success) {
          originalDijelovi.current = originalDijelovi.current.map(r => r.id === editingDio.current!.id ? { ...editingDio.current } : r)
          setUpdatedKatDijelovi(recs => recs.map(r => r.id === editingDio.current!.id ? { ...editingDio.current } : r));
          if (refreshDijelovi) {
            setDioRevisionCounter(rc => rc + 1);
          }
          return Promise.resolve();
        }
        return Promise.reject();
      })
      .catch(err => {
        return Promise.reject();
      });

  }

  const handleKatSelect = (id: number, record: DCRecord | null) => {
    if (id !== -1) {
      return checkIfDioChanged()
        .then((canContinue: boolean) => {
          if (!canContinue) {
            saveEditingDio().catch((error) => {
              snackbarContext.showNotification(`Pogreška kod spremanja dijela! ${JSON.stringify(error)}`, "error", true);
            })
          }
          setKatId(prevKatId => id === prevKatId ? -1 : id);
          katRecord.current = katRecord.current && katRecord.current!.id === id ? null : { ...record };
        })
        .catch(() => {
          console.error("SHOULDN'T HAPPEN!!!");
          setKatId(prevKatId => id === prevKatId ? -1 : id);
          katRecord.current = katRecord.current && katRecord.current.id === id ? null : { ...record };
        });
    } else {
      setKatId(-1);
      katRecord.current = null;
      return Promise.resolve();
    }
  }

  const handleKatChange = (recs: DCRecord[]) => {
    if (recs.length > 0) {
      const rec = recs[0];
      const objekt_id = rec.objekti_id as number;

      const broj_katova = recs.length;
      const broj_dijelova = recs.map(x => x.broj_dijelova as number || 0).reduce((a, b) => a + b, 0);
      const pre_pb = recs.map(x => x.sum_pre_pb as number || 0).reduce((a, b) => a + b, 0);
      //@ts-ignore
      OP3_CALC.setValues(broj_katova, broj_dijelova, pre_pb);
      //@ts-ignore
      OP3_CALC.renderValues();
      //console.timeEnd("OBJECT CHANGE");
    }
  }

  const handleDioSelect = (id: number) => {

  }

  const handleDioChange = (recs: DCRecord[]) => {
    setUpdatedKatDijelovi(recs);
  }

  const handleEditRecord = (value: DCFieldValue, source: string | null) => {
    if (source === null) {
      editingDio.current = null;
      return;
    }
    //@ts-ignore
    if (TIMEOUT !== null) {
      //@ts-ignore
      clearTimeout(TIMEOUT)
    }

    if (dioState.current === 'pending' && tmpEditingDio.current !== null) {
      tmpEditingDio.current = { ...tmpEditingDio.current, [source]: value };
      return;
    }
    TIMEOUT = setTimeout(() => {
      editingDio.current = editingDio.current === null ? null : { ...editingDio.current, [source]: value };
      TIMEOUT = null;
    }, 500);
  }

  const clearSelections = () => {
    //@ts-ignore
    OP3_CALC.resetValues();
    setSelectedRecord(prev => prev === null ? prev : null);
    setSelectedImage(prev => prev === null ? prev : null);
    setCropClip(prev => prev === null ? prev : null);
  }

  const getEditingDioFromTmp = () => {
    editingDio.current = { ...tmpEditingDio.current };
    tmpEditingDio.current = null;
  }

  const refreshRecords = () => {
    const { selectedProject, selectedFishnet } = operationsContext;
    if (selectedProject && selectedFishnet) {
      // loaderContext.toggleLoading(true);
      const url = `operations/projekti/${selectedProject.id}/fishnets/${selectedFishnet.id}/objekti/`
      dc.GetData(url)
        .then((resp) => {
          if (resp.success && Array.isArray(resp.data)) {
            setRecords(resp.data)

            if (selectedRecord && selectedRecord.id) {
              const newValueForSelectedRecord = resp.data.find(x => x.id === selectedRecord.id);
              if (newValueForSelectedRecord) {
                const { broj_katova, broj_dijelova, pre_pb } = newValueForSelectedRecord;
                //@ts-ignore
                OP3_CALC.setValues(broj_katova, broj_dijelova, pre_pb);
                setSelectedRecord(newValueForSelectedRecord);
              } else {
                //@ts-ignore
                OP3_CALC.resetValues();
                setSelectedRecord(null);
              }
            }

            if (resp.data.length === 0) {
              snackbarContext.showNotification("messages.nodata", "warning", true);
            }
          };
        })
        .catch((resp) => {
          console.log(resp);
        })
        .finally(() => {
          // loaderContext.toggleLoading(false);
        });
    } else {
      setRecords([]);
    }
  };

  const handleSetEditingDio = (nEditingDio: DCRecord | null) => {
    tmpEditingDio.current = { ...nEditingDio };
    dioState.current = 'pending';
    checkIfDioChanged()
      .then((canContinue: boolean) => {
        if (!canContinue) {
          saveEditingDio()
            .then(() => {
              dioState.current = 'done';
              getEditingDioFromTmp();
              setDioRevisionCounter(rc => rc + 1);
            })
            .catch((error) => {
              snackbarContext.showNotification(`Pogreška kod spremanja dijela! ${JSON.stringify(error)}`, "error", true);
            })
        } else {
          dioState.current = 'done';
          getEditingDioFromTmp();
        }
      })
      .catch(() => {
        console.error("SHOULDN'T HAPPEN!!!");
        dioState.current = 'done';
        getEditingDioFromTmp();
      });
  }

  const setNapomenaValue = useCallback((newNapomena: DCFieldValue, oldNapomena: DCFieldValue) => {
    napomena.current = [newNapomena, oldNapomena];
  }, [])

  const saveNapomenaValue = useCallback((newNapomena: DCFieldValue) => {
    napomena.current = [newNapomena, newNapomena];
  }, [])

  const toggleMapSize = useCallback(() => {
    setShowLargeMap(prevState => !prevState);
  }, [])

  const handleChangeTabValue = (event: SyntheticEvent<Element, Event>, value: string) => {
    setTabValue(value);
  }

  const handleLoadKN = (selectedRecord: DCRecord) => {
    if (selectedRecord === null || selectedRecord.id === null || selectedRecord.projekt_id === null || selectedRecord.lokacija_id === null ) {
      setKNRecords([]);
    } 

    dcKN.GetData()
      .then((resp) => {
        if (resp.success && Array.isArray(resp.data)) {
          setKNRecords(resp.data);
          if (resp.data.length === 0) {
            snackbarContext.showNotification("messages.nodata_kn", "warning");
          }
        };
      })
      .catch((resp) => {
        console.log(resp);
      });
  }

  const handleLoadZgrade = (selectedRecord: DCRecord) => {
    if (selectedRecord === null || selectedRecord.id === null || selectedRecord.projekt_id === null || selectedRecord.lokacija_id === null ) {
      setZgradeRecords([]);
    } 

    dcZgrade.GetData()
      .then((resp) => {
        if (resp.success && Array.isArray(resp.data)) {
          setZgradeRecords(resp.data);
          if (resp.data.length === 0) {
            snackbarContext.showNotification("messages.nodata_zgrade", "warning");
          }
        };
      })
      .catch((resp) => {
        console.log(resp);
      });
  }

  const handleSelectBuilding = (id: number) => {
    const foundRecord = zgradeRecords.find(x => x.id === id);
    if(foundRecord && foundRecord !== null && foundRecord.wkt && currentZgradaId !== id) {
      const wktFormatter = new OlFormatWKT();
      const feature = wktFormatter.readFeature(foundRecord.wkt, {
        dataProjection: "EPSG:3765",
        featureProjection: "EPSG:3857"
      });
      const src = new OlSourceVector<Geometry>();
      src.addFeature(feature);
      setHighlightedSource(src);
      setCurrentZgradaId(id);
    } else {
      setHighlightedSource(new OlSourceVector<Geometry>());
      setCurrentZgradaId(null);
    }
  }

//#endregion
//#region useEffect

  useEffect(() => {
    // refreshRecords();
  }, []);



  useEffect(() => {
    if (operationsContext.selectedProject === null && selectedRecord !== null && operationsContext.projectItems.length !== 0) {
      const { projekt_id } = selectedRecord;
      if (projekt_id) {
        operationsContext.selectProject(projekt_id as number);
      }
    }
  }, [selectedRecord, operationsContext.projectItems]);

  useEffect(() => {
    if (operationsContext.selectedFishnet === null && selectedRecord !== null && operationsContext.fishnetItems.length !== 0) {
      const { fishnet_id } = selectedRecord;
      if (fishnet_id) {
        dontRefresh.current = true;
        operationsContext.selectFishnet(fishnet_id as number);
      }
    }
  }, [selectedRecord, operationsContext.fishnetItems]);

  useEffect(() => { // korišten za dohvacanje podatka prilikom otvaranja "ostalih objekta" klikom na zelenu tockicu u analizi objekta
    if (operationsContext.selectedFishnet) {
      refreshRecords();

      const { search } = location;
      const urlSearchParams = new URLSearchParams(search);
      const imgIdStr = urlSearchParams.get('image_id');

      const image = imagesBasicInfo.find(x => x.id === parseInt(imgIdStr as string, 10));
      if (imgIdStr !== null) {
        handleImageSelect(parseInt(imgIdStr, 10), image as DCRecord);
      }

    }
  }, [operationsContext.selectedFishnet])

  useEffect(() => {
    if (operationsContext.selectedFishnet) {
      if (dontRefresh.current) {
        dontRefresh.current = false;
      } else {
        refreshRecords();
      }
    } else {
      setRecords([]);
      clearSelections();
    }
  }, [operationsContext.selectedFishnet])

  useEffect(() => {
    if (operationsContext.selectedProject) {
      //do nothing
    } else {
      clearSelections();
    }
  }, [operationsContext.selectedProject]);

  useEffect(() => {
    if (/*records.length > 0 && */!locationUsed4Obj.current) {
      const { search } = location;
      const urlSearchParams = new URLSearchParams(search);
      const objIdStr = urlSearchParams.get('object_id');
      if (objIdStr !== null) {
        handleRecordSelect(parseInt(objIdStr, 10), null);
        locationUsed4Obj.current = true;
      }
    }
  }, [records])

  useEffect(() => {
    if (thumbsLoaded
      && selectedRecord !== null
      && imagesBasicInfo.length !== 0
      && !locationUsed4Img.current
    ) {
      const { search } = location;
      const urlSearchParams = new URLSearchParams(search);
      const imgIdStr = urlSearchParams.get('image_id');
      if (imgIdStr !== null) {
        setSelectedImageId(parseInt(imgIdStr, 10));
        locationUsed4Img.current = true;
      }
    }
  }, [selectedRecord, thumbsLoaded])

  useEffect(() => {
    editingDio.current = null;
  }, [katId])

  // handle loading kn and zgrade
  useEffect(() => {
    setKNRecords([]);
    setZgradeRecords([]);
    setHighlightedSource(new OlSourceVector<Geometry>());

    if (selectedRecord && selectedRecord !== null) {
      handleLoadKN(selectedRecord);
      handleLoadZgrade(selectedRecord);
    }
  }, [selectedRecord])

  const isHeightLargerThanWidth = !!selectedImage && (selectedImage.height as number) > (selectedImage.width as number);

  const isAdmin = userContext?.hasAnyPermission([Permissions.Administration]) ? true : false;
  const isObjektTipForExternalProcess = selectedRecord?.objekt_tip_id !== 1 && !isAdmin;

//#endregion
//#region return

  return (
    <AppPage>
      <div onMouseUp={(evt) => { if (isDrawing) setMouseUp(evt) }}>
        <ObjectAnalysisToolbar
          isDisabled={isPregledanOrPassedQC}
          records={records}
          selectedRecord={selectedRecord}
          onRecordSelect={handleRecordSelect}
          onRecordChange={handleRecordChange}
          checkIfDioChanged={checkIfDioChanged}
          saveEditingDio={saveEditingDio}
          checkIfNapomenaChanged={checkIfNapomenaChanged}
          saveNapomena={saveNapomena}
          openSwalDioNapomena={openSwalDioNapomena}
        />
        <GridContainer sx={{ height: "calc(100vh - 80px)" }}>
          <GridItem xs={6}>
            <Paper elevation={0} sx={{ p: 1, height: tablesMinHeight, minHeight: tablesMinHeight, display: "flex", flexDirection: "column", justifyContent: "space-evenly" }}>
              <div style={{ height: "49%" }}>
                <ObjectAnalysisKatoviTable
                  isDisabled={isPregledanOrPassedQC || isObjektTipForExternalProcess}
                  baseRecordPath={baseRecordPath}
                  baseRecordId={selectedRecord ? selectedRecord.id as number : null}
                  selectedId={katId}
                  onRowSelect={handleKatSelect}
                  onDataChanged={handleKatChange}
                  recalculateKat={updatedKatDijelovi}
                  checkIfDioChanged={checkIfDioChanged}
                  saveEditingDio={saveEditingDio}
                  dijelovi={updatedKatDijelovi}
                />
              </div>
              <div style={{ height: "51%" }}>
                <ObjectAnalysisDijeloviTable
                  isDisabled={isPregledanOrPassedQC || isObjektTipForExternalProcess}
                  baseRecordPath={baseRecordPath}
                  baseRecordId={selectedRecord ? selectedRecord.id as number : null}
                  onRowSelect={handleDioSelect}
                  onDataChanged={handleDioChange}
                  setOriginalDijelovi={(recs: DCRecord[]) => originalDijelovi.current = [...recs]}
                  editingDio={editingDio}
                  setEditingDio={handleSetEditingDio}
                  handleEditRecord={handleEditRecord}
                  katId={katId}
                  katNumber={katRecord.current !== null ? katRecord.current.broj_kata as number : null}
                  propsRevisionCounter={dioRevisionCounter}
                  measurementsSource={measurementsSource}
                />
              </div>
            </Paper>
            <GridContainer style={{ height: showLargeMap ? "calc(100%)" : `calc(100% - ${tablesMinHeight}px)` /* toolbar = 52px, upper container = 525px, margin-top = -8 */ }}>
              <GridItem xs={12} style={{ height: '100%' }}>
                <ObjectAnalysisMiniMap
                  records={records}
                  onObjektSelect={handleRecordSelect}
                  selectedObjekt={selectedRecord}
                  imagesBasicInfo={imagesBasicInfo as DCRecord[]}
                  onImageInfoSelect={handleImageInfoSelect}
                  selectedImage={selectedImage}
                  onToggleMapSize={toggleMapSize}
                  showLargeMap={showLargeMap}
                  mapId={MAP_ID}
                  setLargeCanvas={setLargeCanvas}
                  highlightedSource={highlightedSource}
                  measurementsSource={measurementsSource}
                  setMeasurementsSource={setMeasurementsSource}
                />
              </GridItem>
            </GridContainer>
          </GridItem>
          <GridItem xs={6}>
            <GridContainer columns={24}>
              <GridItem xs={24}>
                <ObjectAnalysisInfo dc={dc}
                  recordId={selectedRecord ? selectedRecord.id as number : undefined}
                  isDisabled={isPregledanOrPassedQC}
                  setNapomenaValue={setNapomenaValue}
                  onSave={saveNapomenaValue}
                  submitValue={saveNapomena}
                  revisionCounter={napomenaRC}
                />
              </GridItem>
              <GridItem xs={largeCanvas ? 24 : isHeightLargerThanWidth ? 14 : 17}>
                <ObjectAnalysisImage
                  mouseUp={mouseUp}
                  baseRecordPath={baseRecordPath}
                  baseRecordId={selectedRecord ? selectedRecord.id as number : null}
                  image={selectedImage}
                  onDrawCropClip={setCropClip}
                  largeCanvas={largeCanvas}
                  setLargeCanvas={setLargeCanvas}
                  drawCropClip={cropClip}
                  setIsDrawing={setIsDrawing}
                />
              </GridItem>
                <GridItem xs={isHeightLargerThanWidth ? 10 : 7}>
                  <Paper elevation={0} sx={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-evenly", gap: 1 }}>
                    <ObjectAnalysisImageCrop
                      ordinal={1}
                      selectedRecord={selectedRecord}
                      cropClip={cropClip}
                      selectedImage={selectedImage}
                      imagesBasicInfo={imagesBasicInfo}
                      onImageInfoSelect={handleImageInfoSelect}
                      onCropSaved={handleCropSaved}
                      isDisabled={isPregledanOrPassedQC}
                      hidden={largeCanvas} 
                      isHeightLargerThanWidth={isHeightLargerThanWidth}
                      />
                    <ObjectAnalysisImageCrop
                      ordinal={2}
                      selectedRecord={selectedRecord}
                      cropClip={cropClip}
                      selectedImage={selectedImage}
                      imagesBasicInfo={imagesBasicInfo}
                      onImageInfoSelect={handleImageInfoSelect}
                      onCropSaved={handleCropSaved}
                      isDisabled={isPregledanOrPassedQC}
                      hidden={largeCanvas} 
                      isHeightLargerThanWidth={isHeightLargerThanWidth}
                      />
                  </Paper>
                </GridItem>
              <GridItem xs={24}>
                <Paper elevation={0} sx={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "start" }}>
                  {!largeCanvas ?
                    <TabContext value={tabValue}>
                      <Tabs
                        value={tabValue}
                        onChange={handleChangeTabValue}
                        TabIndicatorProps={{ style: { backgroundColor: theme.palette.secondary.dark } }}
                      >
                        <StyledTab value={'kn'} label={t('titles.komunalne_obveze')}/>
                        <StyledTab value={'zgrade'} label={t('titles.zgrade')}/>
                        <StyledTab value={'snimke'} label={t('titles.snimke')}/>
                      </Tabs>
                      <Box>
                        <TabPanel value={'kn'} sx={{ padding: 0 }}>
                          <ObjectAnalysisKNTable 
                            records={KNRecords} 
                            dc={dcKN}
                          />
                        </TabPanel>
                        <TabPanel value={'zgrade'} sx={{ padding: 0 }}>
                          <ObjectAnalysisBuildingsTable 
                            records={zgradeRecords} 
                            dc={dcZgrade}
                            onSelectBuilding={handleSelectBuilding}
                          />
                        </TabPanel>
                        <TabPanel value={'snimke'} sx={{ padding: 0 }}>
                          <ObjectAnalysisThumbs
                            baseRecordPath={baseRecordPath}
                            recordId={selectedRecord ? selectedRecord.id as number : null}
                            imagesBasicInfo={imagesBasicInfo}
                            selectedImage={selectedImage}
                            selectedImageId={selectedImageId}
                            selectedImageInfo={selectedImageInfo}
                            onImageSelect={handleImageSelect}
                            setThumbsLoaded={setThumbsLoaded}
                            thumbsLoaded={thumbsLoaded}
                            isDisabled={isPregledanOrPassedQC}
                            handleMarkFavorite={handleMarkFavorite}
                            hidden={largeCanvas}
                          />
                        </TabPanel>
                      </Box>
                    </TabContext>
                    :
                    <></>}
                </Paper>
              </GridItem>
            </GridContainer>
          </GridItem>
        </GridContainer>
      </div>
    </AppPage>
  );
});

export default OperationsObjectAnalysisPage;